import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, Box } from '@material-ui/core';
import { ModalActionsComponent, PasslySelect, PasslyCheckbox, PasslyTextField } from '@app/components';
import { getLoaderBtn } from '@app/store/actions';
import { IAgentDetail, IProvisioningPolicyDTO } from '@app/models';
import { syncService } from '@app/services';
import { useTypedTranslation } from '@app/hooks';
import {
    emptyGuid,
    provisioningStates,
    toBoolean,
    passlyTimeDuration,
    passlyHumanizeDate,
    booleanStringToYesNo,
    created,
    createTranslation,
    onPressEnterModal,
    getLockoutString,
} from '@app/helpers';
const validator = require('validator');

export interface IEditAgentData {
    agent: IAgentDetail;
    editAgentCompleteCallback: Function;
    provisioningPolicies: IProvisioningPolicyDTO[];
}

const initialState = {
    agentName: '',
    agentNameInvalid: false,
    agentStatusSelected: '',
    provisioningPolicySelected: '',
    agentDirectoryPolicy: {},
    changePasswordEnabled: false,
    patchAgentErrorMessage: '',
    patchAgentDone: false,
    shouldReloadAgent: false,
    parsedDirectoryPolicy: {} as any,
    organizationSelected: ''
};

export default function EditSyncAgentDialog(props: IEditAgentData) {
    // -------------------------------------------
    // ----------------- DATA --------------------
    // -------------------------------------------
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const availableOrgs = useSelector((state: any) => state.basic.shellConfig.organizations);

    const [
        {
            agentName,
            agentNameInvalid,
            agentStatusSelected,
            provisioningPolicySelected,
            changePasswordEnabled,
            patchAgentErrorMessage,
            patchAgentDone,
            shouldReloadAgent,
            parsedDirectoryPolicy,
            organizationSelected
        },
        setFormState
    ] = useState(initialState);

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useEffect(() => {
        let directoryPolicy = JSON.parse(props.agent.RawDirectoryPolicy);

        setFormState((previousState) => ({ ...previousState, parsedDirectoryPolicy: directoryPolicy }));
        setFormState((previousState) => ({ ...previousState, agentName: props.agent.AgentName }));
        setFormState((previousState) => ({ ...previousState, agentStatusSelected: props.agent.ProvisioningState }));
        setFormState((previousState) => ({ ...previousState, provisioningPolicySelected: props.agent.ProvisioningPolicyId }));
        setFormState((previousState) => ({ ...previousState, changePasswordEnabled: toBoolean(directoryPolicy['policy.ChangePasswordEnabled']) }));
        setFormState((previousState) => ({ ...previousState, organizationSelected: props.agent.OrganizationId }));
    }, [props.agent]);

    useEffect(() => {
        // Processing complete on page - cleanup and close
        if (patchAgentDone) {
            handleClose();
        }
    }, [patchAgentDone]);

    // -------------------------------------------
    // ----------------- FUNCTIONS ---------------
    // -------------------------------------------
    const onFormChange = (e: any) => {
        const { name, value } = e.target;
        setFormState((previousState) => ({ ...previousState, [name]: value }));
    };

    const onCheckboxChange = (checked: boolean, e: any) => {
        const { name } = e.target;
        setFormState((previousState) => ({ ...previousState, [name]: checked }));
    };

    const onAgentNameChange = (e: any) => {
        let displayName = e.target.value;
        setFormState((previousState) => ({ ...previousState, agentName: displayName }));

        if (validator.isAlphanumeric(validator.blacklist(displayName, ' '))) {
            setFormState((previousState) => ({ ...previousState, agentNameInvalid: false }));
        } else {
            setFormState((previousState) => ({ ...previousState, agentNameInvalid: true }));
        }
    };

    const handlePatchSuccess = (response: any) => {
        setFormState((previousState) => ({ ...previousState, shouldReloadAgent: true }));
        setFormState((previousState) => ({ ...previousState, patchAgentDone: true }));
    };

    const handlePatchError = (error: any) => {
        dispatch(getLoaderBtn(false));
        if (error.response.status === 409) {
            setFormState((previousState) => ({ ...previousState, patchAgentErrorMessage: translation('INVALID_TAKEN_AGENT_NAME') }));
        } else {
            setFormState((previousState) => ({ ...previousState, patchAgentErrorMessage: translation('EDIT_AGENT_ERROR') }));
        }
    };

    const patchAgent = () => {
        parsedDirectoryPolicy['policy.ChangePasswordEnabled'] = changePasswordEnabled;
        let stringifiedPolicy = JSON.stringify(parsedDirectoryPolicy);

        props.agent.AgentName = agentName;
        props.agent.ProvisioningPolicyId = provisioningPolicySelected;
        props.agent.RawDirectoryPolicy = stringifiedPolicy;
        props.agent.ProvisioningState = agentStatusSelected;

        syncService.putSyncAgent(props.agent, props.agent.Id, handlePatchSuccess, handlePatchError);
    };

    const handleSubmitBtn = () => {
        dispatch(getLoaderBtn(true));
        patchAgent();
    };

    const handleClose = () => {
        dispatch(getLoaderBtn(false));
        let shouldReload: boolean = shouldReloadAgent;
        setFormState(initialState);
        props.editAgentCompleteCallback(shouldReload);
    };

    let getPolicyTime = (policyName: string, policyValue: string): string => {
        if (!policyValue) {
            return '';
        }

        let duration: number = passlyTimeDuration(policyValue);

        if (policyName === 'capability.MaximumAge' && duration <= 0) {
            return translation('NO_MAXIMUM');
        }

        return passlyHumanizeDate(policyValue);
    };

    let getPolicyCharacters = (policyValue: string): any => {
        if (!policyValue || policyValue === '') {
            return null;
        }

        let valInt = parseInt(policyValue);

        if (valInt === 1) {
            return valInt + ' ' + translation('CHAR_LEN_SINGLE');
        } else {
            return valInt + ' ' + translation('CHAR_LEN_PLURAL');
        }
    };

    return (
        <>
            <form className='modal__form' method='post' tabIndex={0} onKeyPress={onPressEnterModal(handleSubmitBtn, agentNameInvalid)}>
                <FormControl>
                    <PasslyTextField
                        fullWidth
                        error={agentNameInvalid}
                        name='agentName'
                        value={agentName}
                        onChange={onAgentNameChange}
                        id='agentName-input'
                        helperText={agentNameInvalid ? translation('REQUIRED_FIELD') : ''}
                        label={translation('AGENT_NAME')}
                        autoFocus
                    />
                </FormControl>

                {props.agent.OrganizationId === emptyGuid && props.agent.ProvisioningState === created && (
                    <FormControl>
                        <PasslySelect
                            id='organizationSelected'
                            name='organizationSelected'
                            label={translation('SELECTED_ORGANIZATION')}
                            value={organizationSelected}
                            onChange={onFormChange}
                            options={availableOrgs}
                            optionLabel='name'
                            optionValue='id'
                        />
                    </FormControl>
                )}

                <FormControl>
                    <PasslySelect
                        name='agentStatusSelected'
                        value={agentStatusSelected}
                        onChange={onFormChange}
                        id='select-input'
                        label={'AGENT_STATE'}
                        options={provisioningStates}
                    />
                </FormControl>

                <FormControl>
                    <PasslySelect
                        name='provisioningPolicySelected'
                        value={provisioningPolicySelected}
                        onChange={onFormChange}
                        id='selectProvisioning-input'
                        label={'PROVISIONING_POLICY'}
                        optionLabel='DisplayName'
                        optionValue='Id'
                        options={props.provisioningPolicies}
                        appendTo={document.body}
                    />
                </FormControl>

                {toBoolean(parsedDirectoryPolicy['capability.CanChangePassword']) && (
                    <FormControl>
                            <h4>{translation('SYNC_PASSWORD_POLICY')}</h4>
                            <div className='modal__form'>
                                <PasslyCheckbox
                                    name='changePasswordEnabled'
                                    checked={changePasswordEnabled}
                                    onChange={onCheckboxChange}
                                    label={translation('ENABLE_PASSWORD_WRITEBACK_CHECK')}
                                />
                            </div>
                    </FormControl>
                )}

                {changePasswordEnabled && (
                    <FormControl>
                        <div>
                            <p>
                                <b>{translation('POLICY_NOTE')}</b> {translation('SYNC_ENFORCE_PASS_POLICY')}
                            </p>
                        </div>
                        <dl className='passwordPolicy'>
                            <dt>{translation('PASS_REQUIRES_COMPLEX')}</dt>
                            <dd>{translation(createTranslation(booleanStringToYesNo(parsedDirectoryPolicy['capability.RequiresComplexity'])))}</dd>
                            <dt>{translation('AD_PASS_MIN_AGE')}</dt>
                            <dd>{getPolicyTime('capability.MinimumAge', parsedDirectoryPolicy['capability.MinimumAge'])}</dd>
                            <dt>{translation('AD_PASS_MAX_AGE')}</dt>
                            <dd>{getPolicyTime('capability.MaximumAge', parsedDirectoryPolicy['capability.MaximumAge'])}</dd>
                            <dt>{translation('AD_PASS_MIN_LENGTH')}</dt>
                            <dd>{getPolicyCharacters(parsedDirectoryPolicy['capability.MinimumLength'])}</dd>
                            <dt>{translation('LOCK_AFTER_ATTEMPTS')}</dt>
                            <dd>{getLockoutString(parsedDirectoryPolicy['capability.LockAfterAttempts'], translation)}</dd>
                            {parsedDirectoryPolicy['capability.LockAfterAttempts'] !== '0' && (
                                <>
                                    <dt>{translation('LOCKOUT_DURATION')}</dt>
                                    <dd>{getPolicyTime('capability.LockoutDuration', parsedDirectoryPolicy['capability.LockoutDuration'])}</dd>
                                </>
                            )}
                        </dl>
                    </FormControl>
                )}

                {patchAgentErrorMessage !== '' && (
                    <p>
                        <div className='modal__error'>{patchAgentErrorMessage}</div>
                    </p>
                )}
            </form>

            <ModalActionsComponent
                primaryBtn={translation('SAVE_CHANGES')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleClose}
                disabled={agentNameInvalid || !provisioningPolicySelected}
            />
        </>
    );
}
